import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, CheckCircle, Info, AlertCircle, Copy } from 'lucide-react';
import { Button } from './Button';

export const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true 
}) => {
  useEffect(() => {
    if (!closeOnEscape) return;
    if (!isOpen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal-900/55 p-2 backdrop-blur-sm sm:p-4"
      onClick={handleOverlayClick}
    >
      <div
        className={`bg-white rounded-3xl border border-white/70 shadow-2xl shadow-charcoal-900/20 w-full ${sizeClasses[size]} max-h-[90vh] overflow-y-auto`}
        role="dialog"
        aria-modal="true"
      >
        {(title || showCloseButton) && (
          <div className="flex items-center justify-between gap-3 p-4 sm:p-6 border-b border-neutral-200">
            {title && <h2 className="min-w-0 break-words text-lg font-heading font-bold text-charcoal-900">{title}</h2>}
            {showCloseButton && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-neutral-100 rounded-xl transition-colors"
                aria-label="Close modal"
              >
                <X className="h-5 w-5 text-charcoal-400" />
              </button>
            )}
          </div>
        )}
        <div className="p-4 sm:p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

export const AlertModal = ({ 
  isOpen, 
  onClose, 
  title, 
  message, 
  type = 'info',
  confirmText = 'OK',
  code = ''
}) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) setCopied(false);
  }, [isOpen]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const icons = {
    success: <CheckCircle className="h-6 w-6 text-success" />,
    error: <AlertCircle className="h-6 w-6 text-alert" />,
    warning: <AlertTriangle className="h-6 w-6 text-warning" />,
    info: <Info className="h-6 w-6 text-primary" />
  };

  const titleColors = {
    success: 'text-success',
    error: 'text-alert',
    warning: 'text-warning',
    info: 'text-charcoal-700'
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size={code ? 'md' : 'sm'}
      closeOnOverlayClick={false}
      showCloseButton={false}
    >
      <div className="text-center">
        <div className="flex justify-center mb-4">
          {icons[type]}
        </div>
        {title && (
          <h3 className={`text-lg font-semibold mb-3 ${titleColors[type]}`}>
            {title}
          </h3>
        )}
        <p className="text-charcoal-600 mb-6">
          {message}
        </p>
        {code && (
          <div className="mb-6 text-left">
            <p className="mb-2 text-xs font-semibold text-charcoal-600">Form address</p>
            <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-xl bg-charcoal-900 p-3 text-left font-mono text-xs text-success-200">{code}</pre>
            <Button
              type="button"
              variant="outline"
              onClick={handleCopy}
              className="mt-3 w-full"
              aria-label={copied ? 'Code copied' : 'Copy form code'}
            >
              {copied ? (
                <>
                  <CheckCircle className="h-4 w-4 text-success" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  Copy
                </>
              )}
            </Button>
          </div>
        )}
        <Button onClick={onClose} className="w-full">
          {confirmText}
        </Button>
      </div>
    </Modal>
  );
};

export const ConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  isDestructive = false 
}) => {
  const icons = {
    warning: <AlertTriangle className="h-6 w-6 text-warning" />,
    danger: <AlertCircle className="h-6 w-6 text-alert" />,
    info: <Info className="h-6 w-6 text-primary" />
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size="sm"
      closeOnOverlayClick={false}
      showCloseButton={false}
    >
      <div className="text-center">
        <div className="flex justify-center mb-4">
          {icons[type]}
        </div>
        {title && (
          <h3 className="text-lg font-semibold mb-3 text-charcoal-700">
            {title}
          </h3>
        )}
        <p className="text-charcoal-600 mb-6">
          {message}
        </p>
        <div className="flex flex-col-reverse gap-3 min-[360px]:flex-row">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="flex-1"
          >
            {cancelText}
          </Button>
          <Button 
            onClick={handleConfirm}
            variant={isDestructive ? "destructive" : "default"}
            className="flex-1"
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
}; 